import { useEffect, useRef, useState } from 'react'
import {
  MenuList,
  MenuItem,
  Menu,
  MenuButton,
  IconButton,
  HStack,
  chakra,
  VStack,
  Text,
  Avatar,
  Button
} from '@chakra-ui/react'
import cx from 'classnames'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import useBreakPoint from 'hooks/useBreakPoint'
import { useStores } from 'hooks/useStores'
import get from 'lodash/get'
import last from 'lodash/last'
import { observer } from 'mobx-react'
import { useLocation, useNavigate } from 'react-router-dom'
import colors from 'theme/colors.theme'
import SvgIcon from 'components/SvgIcon'
import { S3FileTypeEnum } from 'constants/aws'
import { EBreakPoint } from 'constants/theme'
import { IUser } from 'interfaces/user'
import { IMessageGroupThreads, ISupportThreadDto } from 'interfaces/message'
import { EInboxTab, ESupportThreadSortBy } from 'pages/SupportInboxPage/constants'
import routes from 'routes'
import { getS3FileUrl } from 'utils/images'
import { getName } from 'utils/users'
import styles from './styles.module.scss'
import { IMessageNotification } from './constants'
import { getValidArray } from 'utils/common'
import { AuthRoleIdEnum } from 'constants/user'
dayjs.extend(relativeTime)

const MessageButton = () => {
  const menuRef = useRef<HTMLDivElement>(null)
  const { authStore, messageStore, organizationStore, processStore } = useStores()
  const { userDetail } = authStore
  const { unreadGeneralThreadCount, unreadSupportThreadCount, generalMessageThreads, supportThreads } = messageStore
  const { organization } = organizationStore
  const organizationId = organization?.id ?? 0
  const currentTheme = organization?.theme ?? {}
  const currentColor = currentTheme?.primaryColor ?? 'primary.500'
  const navigate = useNavigate()
  const location = useLocation()
  const pathName = location.pathname
  const isInboxPage = pathName.includes(routes.messages.value)
  const isMessageFullAccess = userDetail?.isMessageFullAccess
  const isBasicUser = userDetail?.authRoleId === AuthRoleIdEnum.BASIC_USER

  const [messageNotifications, setMessageNotifications] = useState<IMessageNotification[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [unreadMessages, setUnreadMessages] = useState<{
    general: IMessageGroupThreads[],
    support: ISupportThreadDto[]
  }>({ general: [], support: [] })

  const isCallingApi = useRef(false)
  const hasInitiallyFetched = useRef(false)

  async function fetchData() {
    if (isCallingApi.current) {
      return
    }

    isCallingApi.current = true
    await Promise.all([
      messageStore.getUnreadThread(),
      messageStore.fetchGeneralMessageThreads(20, '', true),
      messageStore.getSupportMessageThreads(organizationId, 20, true),
      messageStore.getAllUnreadMessages().then(result => {
        setUnreadMessages({
          general: result.unreadGeneralMessages,
          support: result.unreadSupportMessages
        })
      })
    ])
    isCallingApi.current = false
  }

  function handleClickMessage(notification: IMessageNotification): void {
    const { messageThreadId, type } = notification
    
    // Use the same navigation logic as push notifications for consistency
    if (type === EInboxTab.SUPPORT) {
      messageStore.setCurrentSupportThreadId(messageThreadId)
      navigate(`${routes.messages.value}?tab=${type}&threadId=${messageThreadId}`)
    } else {
      messageStore.setCurrentGeneralThreadId(messageThreadId)
      navigate(`${routes.messages.value}?tab=${type}&threadId=${messageThreadId}`)
    }
  }

  function handleRouteToProcessDetail(event, processId: number, stepId?: number): void {
    event.stopPropagation()

    processStore.setExpandingSteps([])
    if (stepId) {
      processStore.setExpandingSteps([stepId])
    }
    navigate(routes.processes.processId.value(String(processId)))
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuRef])

  useEffect(() => {
    if (isInboxPage) {
      return () => {}
    }

    // Initial fetch only once - Firebase will handle real-time updates
    if (!hasInitiallyFetched.current) {
      if (!isBasicUser || isMessageFullAccess) {
        messageStore.isUnclaimed = true
        messageStore.isClaimedByMe = true
        messageStore.isClaimedByOthers = true
        messageStore.changeSortBy(ESupportThreadSortBy.NEWEST)
      }
      fetchData()
      hasInitiallyFetched.current = true
    }

    return () => {
      messageStore.isUnclaimed = false
      messageStore.isClaimedByMe = false
      messageStore.isClaimedByOthers = false
    }
  }, [pathName]) // Only depend on pathName to reset when navigating to/from inbox

  useEffect(() => {
    const unreadGeneralNotifications: IMessageNotification[] = unreadMessages.general
              .filter(generalMessageThread => generalMessageThread && generalMessageThread.id)
      .map(generalMessageThread => {
        const latestMessage = last(generalMessageThread?.groupMessages) ?? generalMessageThread?.groupMessages?.[0]
        
        if (!latestMessage || !latestMessage.createdAt) {
          return {
            sender: {} as IUser,
            messageThreadId: generalMessageThread.id,
            type: EInboxTab.GENERAL,
            createdAt: new Date(),
            isSeen: false,
            isNotReply: false,
            isUnread: true
          }
        }

        return {
          sender: latestMessage?.user,
          messageThreadId: generalMessageThread.id,
          type: EInboxTab.GENERAL,
          createdAt: latestMessage.createdAt,
          isSeen: false,
          isNotReply: latestMessage?.userId === userDetail?.id,
          isUnread: true 
        }
      })

    const unreadSupportNotifications: IMessageNotification[] = unreadMessages.support
      .filter(supportThread => supportThread && supportThread.thread?.id) 
      .map(supportThread => {
        const latestMessage = get(supportThread?.thread, `supportMessage`)
        const latestMessageUser: IUser = get(latestMessage, 'user')

        if (!latestMessage || !latestMessage.createdAt) {
          return {
            sender: {} as IUser,
            messageThreadId: supportThread.thread.id,
            type: EInboxTab.SUPPORT,
            createdAt: new Date(),
            isSeen: false,
            processId: supportThread.thread.step?.processId ?? supportThread.thread.processId,
            stepId: supportThread.thread.stepId,
            supportThread: supportThread.thread,
            isNotReply: false,
            isUnread: true
          }
        }

        return {
          sender: latestMessageUser,
          messageThreadId: supportThread.thread.id,
          type: EInboxTab.SUPPORT,
          createdAt: latestMessage.createdAt,
          isSeen: false,
          processId: supportThread.thread.step?.processId ?? supportThread.thread.processId,
          stepId: supportThread.thread.stepId,
          supportThread: supportThread.thread,
          isNotReply: latestMessage.userId === userDetail?.id,
          isUnread: true
        }
      })

    const generalNotifications: IMessageNotification[] = generalMessageThreads
      .filter(generalMessageThread => generalMessageThread && generalMessageThread.id)
      .map(generalMessageThread => {
        const latestMessage = last(generalMessageThread?.groupMessages) ?? generalMessageThread?.groupMessages?.[0]

        if (!latestMessage || !latestMessage.createdAt) {
          return {
            sender: {} as IUser,
            messageThreadId: generalMessageThread.id,
            type: EInboxTab.GENERAL,
            createdAt: new Date(),
            isSeen: generalMessageThread.isSeen,
            isNotReply: false,
            isUnread: false
          }
        }

        return {
          sender: latestMessage?.user,
          messageThreadId: generalMessageThread.id,
          type: EInboxTab.GENERAL,
          createdAt: latestMessage.createdAt,
          isSeen: generalMessageThread.isSeen,
          isNotReply: latestMessage?.userId === userDetail?.id,
          isUnread: false
        }
      })

    const supportNotifications: IMessageNotification[] = supportThreads
      .filter(supportThread => supportThread && supportThread.thread?.id)
      .map(supportThread => {
        const latestMessage = get(supportThread?.thread, `supportMessage`)
        const latestMessageUser: IUser = get(latestMessage, 'user')

        if (!latestMessage || !latestMessage.createdAt) {
          return {
            sender: {} as IUser,
            messageThreadId: supportThread.thread.id,
            type: EInboxTab.SUPPORT,
            createdAt: new Date(),
            isSeen: supportThread.isSeen,
            processId: supportThread.thread.step?.processId ?? supportThread.thread.processId,
            stepId: supportThread.thread.stepId,
            supportThread: supportThread.thread,
            isNotReply: false,
            isUnread: false
          }
        }

        return {
          sender: latestMessageUser,
          messageThreadId: supportThread.thread.id,
          type: EInboxTab.SUPPORT,
          createdAt: latestMessage.createdAt,
          isSeen: supportThread.isSeen,
          processId: supportThread.thread.step?.processId ?? supportThread.thread.processId,
          stepId: supportThread.thread.stepId,
          supportThread: supportThread.thread,
          isNotReply: latestMessage.userId === userDetail?.id,
          isUnread: false
        }
      })

    const allNotifications = [
      ...unreadGeneralNotifications,
      ...unreadSupportNotifications,
      ...generalNotifications,
      ...supportNotifications
    ]
      .sort((a, b) => {
        if (a.isUnread && !b.isUnread) return -1
        if (!a.isUnread && b.isUnread) return 1
        return dayjs(b.createdAt).diff(dayjs(a.createdAt))
      })
      .filter(notification => !notification.isNotReply)

    setMessageNotifications(allNotifications)
  }, [unreadMessages, generalMessageThreads, supportThreads])

  return (
    <div ref={menuRef} className={styles.container}>
      <Menu isOpen={isOpen}>
        {unreadGeneralThreadCount + unreadSupportThreadCount > 0 && (
          <div className={styles.notificationCount}>{unreadGeneralThreadCount + unreadSupportThreadCount}</div>
        )}
        <MenuButton
          onClick={() => setIsOpen(!isOpen)}
          as={IconButton}
          aria-label="Messages"
          background="transparent"
          _hover={{ background: 'transparent' }}
          _active={{ background: 'transparent' }}
          border="none"
          icon={<SvgIcon iconName="inbox-button" size={40} />}
          variant="outline"
        />

        <MenuList onClick={() => setIsOpen(false)} className={styles.menu} position="relative">
          <HStack
            justifyContent="space-between"
            padding="12px 16px"
            borderBottom="1px solid #E2E8F0"
            fontSize={16}
            fontWeight={600}
          >
            <Text color="gray.800">Messages</Text>
          </HStack>
          <VStack spacing={0} marginTop={3}>
            {messageNotifications.map((notification, index) => {
              const senderName = getName(notification.sender)
              const processId = notification.processId ?? 0
              const stepId = notification.stepId
              const isThreadReply =
                notification.type === EInboxTab.SUPPORT &&
                getValidArray(notification.supportThread?.supportMessages).length > 1

              return (
                <MenuItem
                  key={index}
                  paddingY={4}
                  background={notification.isUnread ? 'primary.50' : (notification.isSeen ? 'white' : 'primary.50')}
                  border="none"
                  _hover={{ background: colors.primary[50] }}
                  className={cx({ [styles.unseen]: !notification.isSeen || notification.isUnread })}
                >
                  <HStack
                    width="100%"
                    key={notification.messageThreadId}
                    justifyContent="space-between"
                    alignItems="flex-start"
                    onClick={() => handleClickMessage(notification)}
                  >
                    <HStack alignItems="center">
                      <Avatar
                        size="sm"
                        name={senderName}
                        src={getS3FileUrl(S3FileTypeEnum.IMAGE, organizationId, notification.sender?.image ?? '')}
                      />
                      {notification.type === EInboxTab.SUPPORT && (
                        <Text
                          color="gray.700"
                          fontSize="md"
                          fontWeight={600}
                          lineHeight={6}
                          overflow="hidden"
                          textOverflow="ellipsis"
                        >
                          {senderName}
                          {isThreadReply && (
                            <chakra.span color="gray.700" fontSize="md" fontWeight={500}>
                              &nbsp;has replied to your thread
                            </chakra.span>
                          )}
                          {!isThreadReply && (
                            <>
                              <chakra.span color="gray.700" fontSize="md" fontWeight={500}>{` has commented ${
                                notification.stepId || processId ? 'on ' : ''
                              }`}</chakra.span>
                              {notification.processId && !notification.stepId && (
                                <chakra.span
                                  color="gray.700"
                                  fontSize="md"
                                  fontWeight={600}
                                  _hover={{ color: currentColor }}
                                  onClick={event => handleRouteToProcessDetail(event, processId)}
                                >{`${notification.supportThread?.process?.name}`}</chakra.span>
                              )}
                              {notification.stepId && (
                                <chakra.span
                                  color="gray.700"
                                  fontSize="md"
                                  fontWeight={600}
                                  _hover={{ color: currentColor }}
                                  onClick={event => handleRouteToProcessDetail(event, processId, stepId)}
                                >{`${notification.supportThread?.step?.process?.name} | ${notification.supportThread?.step?.name} `}</chakra.span>
                              )}
                            </>
                          )}
                        </Text>
                      )}
                      {notification.type === EInboxTab.GENERAL && (
                        <Text
                          color="gray.700"
                          fontSize="md"
                          fontWeight={600}
                          lineHeight={6}
                          overflow="hidden"
                          textOverflow="ellipsis"
                        >
                          {senderName}
                          <chakra.span
                            color="gray.700"
                            fontSize="md"
                            fontWeight={500}
                          >{` has sent you message`}</chakra.span>
                        </Text>
                      )}
                    </HStack>
                    <HStack>
                      <Text minWidth="60px">{dayjs(notification?.createdAt).fromNow(true)}</Text>
                    </HStack>
                  </HStack>
                </MenuItem>
              )
            })}
          </VStack>
          <HStack width="100%" position="fixed" bottom={0} background="white" zIndex={2} className={styles.footer}>
            <Button
              width="100%"
              margin={4}
              border="1px solid"
              borderColor="gray.200"
              background="white"
              _hover={{ background: currentColor }}
              onClick={() => navigate(routes.messages.value)}
            >
              Go to inbox
            </Button>
          </HStack>
        </MenuList>
      </Menu>
    </div>
  )
}

export default observer(MessageButton)
